import { useState } from 'react'
import {
  ChevronDown, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2,
  XCircle, Activity, Shield, FileText, CreditCard, MapPin, Lock, Unlock,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// ─── Types (mirrors backend structuredData.ts CountryProfile) ────────

interface ProductionSourceData {
  source: string
  vendor: string
  total_checks: number
  fields: { field: string; pass_rate: number; total: number; pass?: number; fail?: number; partial?: number }[]
}

interface SourceProfile {
  name: string
  vendor: string
  coverage: number | null
  consent_required: boolean
  government_id: boolean
  api_status: string
  mandatory_pii: string[]
  verifies: string[]
  address: { street: boolean; city: boolean; zip: boolean; state: boolean; status: string | null } | null
  notes: string | null
  production: ProductionSourceData | null
}

interface RulesetAssessment {
  achievable: boolean
  strength: number
  strength_label: 'strong' | 'decent' | 'weak' | 'not_achievable'
  sources: { name: string; vendor: string; coverage: number; consent_required: boolean }[]
  required_pii: string[]
  any_consent_free: boolean
  warnings: string[]
}

interface GovIdInfo {
  vendor: string
  source: string | null
  id_type: string
  format: string | null
  characters: string | null
  regex: string | null
}

interface AddressSummary {
  best_status: string | null
  sources: {
    name: string; vendor: string
    street: boolean; city: boolean; zip: boolean; state: boolean
    status: string | null; notes: string | null
  }[]
}

interface ProductionProfile {
  entity_pass_rate: number
  best_source: string | null
  total_entities: number
  pass: number
  fail: number
  refer: number
  trend: 'IMPROVING' | 'STABLE' | 'DECLINING' | null
  trend_diff: number | null
  data_source?: 'benchmark' | 'all_customers' | null
  brackets: { label: string; pass_rate: number; total: number; best_source: string | null }[]
  top_sources: ProductionSourceData[]
}

interface ConsentSummary {
  has_consent_free: boolean
  consent_free_sources: { name: string; vendor: string; coverage: number | null }[]
  consent_required_sources: { name: string; vendor: string; coverage: number | null }[]
}

interface CountryProfile {
  code: string
  name: string
  confidence: 'high' | 'medium' | 'low' | 'none'
  recommended_vendor: string | null
  vendor_notes: string | null
  production: ProductionProfile | null
  one_plus_one: RulesetAssessment
  two_plus_two: RulesetAssessment
  sources: SourceProfile[]
  consent: ConsentSummary
  gov_ids: GovIdInfo[]
  address: AddressSummary
  warnings: string[]
}

interface StructuredData {
  answer_type: string
  ruleset_asked?: string | null
  user_fields?: string[] | null
  countries: CountryProfile[]
}

export interface StructuredAnswerProps {
  answer_type: string
  structured_data: StructuredData | null
  ai_answer: string
}

// ─── Design tokens ──────────────────────────────────────────────────

const CONFIDENCE = {
  high:   { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'High' },
  medium: { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30',   label: 'Medium' },
  low:    { bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/30',  label: 'Low' },
  none:   { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30',     label: 'None' },
}

const STRENGTH = {
  strong:          { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Strong' },
  decent:          { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400',   label: 'Decent' },
  weak:            { bg: 'bg-orange-500/10',  border: 'border-orange-500/30',  text: 'text-orange-400',  label: 'Weak' },
  not_achievable:  { bg: 'bg-red-500/10',     border: 'border-red-500/30',     text: 'text-red-400',     label: 'Not achievable' },
}

function rateColor(rate: number) {
  if (rate >= 70) return 'text-emerald-400'
  if (rate >= 40) return 'text-amber-400'
  return 'text-red-400'
}

function rateBg(rate: number) {
  if (rate >= 70) return 'bg-emerald-500'
  if (rate >= 40) return 'bg-amber-500'
  return 'bg-red-500'
}

// ─── Reusable micro-components ──────────────────────────────────────

function Pill({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${className}`}>{children}</span>
}

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon size={13} className="text-violet-400 flex-shrink-0" />
      <span className="text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider">{title}</span>
      {subtitle && <span className="text-[9px] text-[var(--text-muted)]">{subtitle}</span>}
    </div>
  )
}

function MiniBar({ rate, width = 'w-16' }: { rate: number; width?: string }) {
  return (
    <div className={`${width} h-1.5 rounded-full bg-[var(--bg-tertiary)] overflow-hidden`}>
      <div className={`h-full rounded-full ${rateBg(rate)}`} style={{ width: `${Math.min(rate, 100)}%` }} />
    </div>
  )
}

function BoolIcon({ value }: { value: boolean }) {
  return value
    ? <CheckCircle2 size={11} className="text-emerald-400" />
    : <XCircle size={11} className="text-red-400/50" />
}

// ─── Production section (always shown when data exists) ─────────────

function ProductionSection({ prod }: { prod: ProductionProfile }) {
  // Use the best source's counts for the bar so it matches the headline rate
  const bestSrc = prod.best_source
    ? prod.top_sources.find(s => s.source === prod.best_source)
    : null
  const bestFields = bestSrc?.fields[0]
  const barPass = bestFields?.pass ?? prod.pass
  const barFail = bestFields?.fail ?? prod.fail
  const barRefer = bestFields?.partial ?? prod.refer
  const barTotal = barPass + barFail + barRefer

  const passPct = barTotal > 0 ? Math.round((barPass / barTotal) * 100) : 0
  const failPct = barTotal > 0 ? Math.round((barFail / barTotal) * 100) : 0
  const referPct = barTotal > 0 ? 100 - passPct - failPct : 0

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
      <SectionHeader icon={Activity} title="Production Data" subtitle={prod.data_source === 'benchmark' ? 'ThoughtSpot — benchmark customer' : 'ThoughtSpot — all customers'} />
      {prod.data_source === 'benchmark' && (
        <div className="mb-2 flex items-center gap-1.5">
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400 font-medium">Benchmark</span>
          <span className="text-[9px] text-[var(--text-muted)]">Data from our highest-volume, best-configured production customer</span>
        </div>
      )}

      {/* Top row: big pass rate + breakdown bar + trend */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex flex-col items-center">
          <span className={`text-3xl font-bold ${rateColor(prod.entity_pass_rate)}`}>{prod.entity_pass_rate}%</span>
          <span className="text-[9px] text-[var(--text-muted)]">{prod.best_source ? `Best: ${prod.best_source}` : 'Best source pass rate'}</span>
        </div>

        {/* Stacked bar — matches the best source's counts */}
        <div className="flex-1">
          <div className="flex h-5 rounded-full overflow-hidden">
            {passPct > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${passPct}%` }} />}
            {referPct > 0 && <div className="bg-amber-500 transition-all" style={{ width: `${referPct}%` }} />}
            {failPct > 0 && <div className="bg-red-500 transition-all" style={{ width: `${failPct}%` }} />}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[9px] text-emerald-400">{barPass.toLocaleString()} pass</span>
            <span className="text-[9px] text-amber-400">{barRefer.toLocaleString()} refer</span>
            <span className="text-[9px] text-red-400">{barFail.toLocaleString()} fail</span>
          </div>
        </div>

        {/* Trend */}
        {prod.trend && (
          <div className="flex flex-col items-center">
            {prod.trend === 'IMPROVING' && <TrendingUp size={18} className="text-emerald-400" />}
            {prod.trend === 'DECLINING' && <TrendingDown size={18} className="text-red-400" />}
            {prod.trend === 'STABLE' && <Minus size={18} className="text-[var(--text-muted)]" />}
            <span className={`text-[9px] ${prod.trend === 'IMPROVING' ? 'text-emerald-400' : prod.trend === 'DECLINING' ? 'text-red-400' : 'text-[var(--text-muted)]'}`}>
              {prod.trend_diff != null ? `${prod.trend_diff > 0 ? '+' : ''}${prod.trend_diff}pp` : prod.trend.toLowerCase()}
            </span>
          </div>
        )}
      </div>

      {/* Time brackets */}
      {prod.brackets.length > 1 && (
        <div className="flex gap-2 mb-3">
          {prod.brackets.map((b, i) => (
            <div key={i} className="flex-1 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1.5 text-center">
              <div className={`text-sm font-bold ${rateColor(b.pass_rate)}`}>{b.pass_rate}%</div>
              <MiniBar rate={b.pass_rate} width="w-full" />
              <div className="text-[8px] text-[var(--text-muted)] mt-1">{b.label}</div>
              <div className="text-[8px] text-[var(--text-muted)]">{b.total.toLocaleString()}</div>
              {b.best_source && <div className="text-[7px] text-violet-400 truncate mt-0.5">{b.best_source}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Source breakdown (if available) */}
      {prod.top_sources.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-[var(--text-primary)] mb-1.5">Sources in production</div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-[var(--text-muted)] text-left border-b border-[var(--border-color)]">
                <th className="pb-1 font-medium">Source</th>
                <th className="pb-1 font-medium text-right">Checks</th>
                <th className="pb-1 font-medium text-right">Pass Rate</th>
                <th className="pb-1 font-medium" style={{ width: '35%' }}></th>
                <th className="pb-1 font-medium text-right">Pass</th>
                <th className="pb-1 font-medium text-right">Fail</th>
                <th className="pb-1 font-medium text-right">Refer</th>
              </tr>
            </thead>
            <tbody>
              {prod.top_sources.map((src, i) => {
                const f = src.fields[0] // entity-level stats
                const passRate = f?.pass_rate ?? 0
                return (
                  <tr key={i} className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 font-medium text-[var(--text-primary)]">{src.source}</td>
                    <td className="py-1.5 text-right text-[var(--text-muted)]">{src.total_checks.toLocaleString()}</td>
                    <td className={`py-1.5 text-right font-bold ${rateColor(passRate)}`}>{passRate}%</td>
                    <td className="py-1.5 px-2"><MiniBar rate={passRate} width="w-full" /></td>
                    <td className="py-1.5 text-right text-emerald-400">{f?.pass?.toLocaleString() ?? 0}</td>
                    <td className="py-1.5 text-right text-red-400">{f?.fail?.toLocaleString() ?? 0}</td>
                    <td className="py-1.5 text-right text-amber-400">{f?.partial?.toLocaleString() ?? 0}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[9px] text-[var(--text-muted)] mt-2">{barTotal.toLocaleString()} entities via {prod.best_source || 'best source'} · {prod.total_entities.toLocaleString()} total across all sources</div>
    </div>
  )
}

// ─── Ruleset section ────────────────────────────────────────────────

function RulesetRow({ label, assessment }: { label: string; assessment: RulesetAssessment }) {
  const s = STRENGTH[assessment.strength_label]
  return (
    <div className={`rounded-lg border ${s.border} ${s.bg} px-3 py-2.5`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-[var(--text-primary)]">{label}</span>
          <Pill className={`${s.bg} ${s.text} ${s.border}`}>{s.label}</Pill>
          {assessment.achievable && assessment.strength > 0 && (
            <span className={`text-sm font-bold ${s.text}`}>{assessment.strength}% <span className="text-[9px] font-normal text-[var(--text-muted)]">coverage</span></span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {assessment.any_consent_free
            ? <Pill className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"><Unlock size={9} className="inline mr-0.5" />No consent option</Pill>
            : <Pill className="bg-amber-500/10 text-amber-400 border-amber-500/30"><Lock size={9} className="inline mr-0.5" />Consent required</Pill>
          }
        </div>
      </div>

      {assessment.sources.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {assessment.sources.map((src, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-secondary)]">
              {src.name} <span className="text-[var(--text-muted)]">({src.vendor})</span>
              {src.coverage > 0 && <span className="text-[var(--text-muted)]"> {src.coverage}% cov</span>}
              {src.consent_required && <Lock size={8} className="inline ml-1 text-amber-400" />}
            </span>
          ))}
        </div>
      )}

      {assessment.required_pii.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] text-[var(--text-muted)]">Required:</span>
          {assessment.required_pii.map((pii, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20">{pii}</span>
          ))}
        </div>
      )}

      {assessment.warnings.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {assessment.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1 text-[9px] text-amber-400/80">
              <AlertTriangle size={9} className="flex-shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RulesetsSection({ onePlusOne, twoPlusTwo, rulesetAsked }: { onePlusOne: RulesetAssessment; twoPlusTwo: RulesetAssessment; rulesetAsked?: string | null }) {
  // Only show the ruleset the user asked about, or both if they didn't specify
  const showOnePlusOne = !rulesetAsked || rulesetAsked === '1+1' || rulesetAsked === 'any'
  const showTwoPlusTwo = !rulesetAsked || rulesetAsked === '2+2' || rulesetAsked === 'any'

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
      <SectionHeader icon={Shield} title="Rulesets" />
      <div className="space-y-2">
        {showOnePlusOne && <RulesetRow label="1+1" assessment={onePlusOne} />}
        {showTwoPlusTwo && <RulesetRow label="2+2" assessment={twoPlusTwo} />}
      </div>
    </div>
  )
}

// ─── Consent section ────────────────────────────────────────────────

function ConsentSection({ consent }: { consent: ConsentSummary }) {
  return (
    <div className={`rounded-lg border p-3 ${consent.has_consent_free ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
      <div className="flex items-center gap-2 mb-2">
        {consent.has_consent_free
          ? <><Unlock size={13} className="text-emerald-400" /><span className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider">Consent-free options available</span></>
          : <><Lock size={13} className="text-amber-400" /><span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">All sources require consent</span></>
        }
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {consent.consent_free_sources.map((s, i) => (
          <div key={`free-${i}`} className="flex items-center gap-1.5 text-[10px] px-2 py-1 rounded border border-emerald-500/20 bg-emerald-500/5">
            <Unlock size={9} className="text-emerald-400" />
            <span className="text-[var(--text-secondary)]">{s.name}</span>
            <span className="text-[var(--text-muted)]">({s.vendor})</span>
            {s.coverage != null && <span className="text-[var(--text-muted)] ml-auto">{s.coverage}%</span>}
          </div>
        ))}
        {consent.consent_required_sources.map((s, i) => (
          <div key={`req-${i}`} className="flex items-center gap-1.5 text-[10px] px-2 py-1 rounded border border-amber-500/20 bg-amber-500/5">
            <Lock size={9} className="text-amber-400" />
            <span className="text-[var(--text-secondary)]">{s.name}</span>
            <span className="text-[var(--text-muted)]">({s.vendor})</span>
            {s.coverage != null && <span className="text-[var(--text-muted)] ml-auto">{s.coverage}%</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Gov ID section ─────────────────────────────────────────────────

function GovIdSection({ govIds }: { govIds: GovIdInfo[] }) {
  if (govIds.length === 0) return null
  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
      <SectionHeader icon={CreditCard} title="Government IDs" />
      <table className="w-full text-[10px]">
        <thead>
          <tr className="text-[var(--text-muted)] text-left border-b border-[var(--border-color)]">
            <th className="pb-1 font-medium">ID Type</th>
            <th className="pb-1 font-medium">Vendor</th>
            <th className="pb-1 font-medium">Format</th>
            <th className="pb-1 font-medium">Characters</th>
          </tr>
        </thead>
        <tbody>
          {govIds.map((id, i) => (
            <tr key={i} className="border-b border-[var(--border-color)]/50">
              <td className="py-1.5 font-medium text-[var(--text-primary)]">{id.id_type}</td>
              <td className="py-1.5 text-[var(--text-muted)]">{id.vendor}{id.source && ` (${id.source})`}</td>
              <td className="py-1.5 font-mono text-violet-300">{id.format || '—'}</td>
              <td className="py-1.5 text-[var(--text-muted)]">{id.characters || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {govIds.some(id => id.regex) && (
        <div className="mt-1.5 space-y-0.5">
          {govIds.filter(id => id.regex).map((id, i) => (
            <div key={i} className="text-[9px] text-[var(--text-muted)]">
              {id.id_type} regex: <code className="text-violet-300 bg-violet-500/10 px-1 rounded">{id.regex}</code>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Address section ────────────────────────────────────────────────

function AddressSection({ address }: { address: AddressSummary }) {
  if (address.sources.length === 0) return null

  const statusColor = (s: string | null) => {
    if (!s) return 'text-[var(--text-muted)]'
    const lower = s.toLowerCase()
    if (lower.includes('full') || lower.includes('performing')) return 'text-emerald-400'
    if (lower.includes('risk')) return 'text-amber-400'
    return 'text-red-400'
  }

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
      <SectionHeader icon={MapPin} title="Address Verification" />
      <table className="w-full text-[10px]">
        <thead>
          <tr className="text-[var(--text-muted)] text-left border-b border-[var(--border-color)]">
            <th className="pb-1 font-medium">Source</th>
            <th className="pb-1 font-medium">Vendor</th>
            <th className="pb-1 font-medium text-center">Street</th>
            <th className="pb-1 font-medium text-center">City</th>
            <th className="pb-1 font-medium text-center">Zip</th>
            <th className="pb-1 font-medium text-center">State</th>
            <th className="pb-1 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {address.sources.map((src, i) => (
            <tr key={i} className="border-b border-[var(--border-color)]/50">
              <td className="py-1.5 font-medium text-[var(--text-primary)]">{src.name}</td>
              <td className="py-1.5 text-[var(--text-muted)]">{src.vendor}</td>
              <td className="py-1.5 text-center"><BoolIcon value={src.street} /></td>
              <td className="py-1.5 text-center"><BoolIcon value={src.city} /></td>
              <td className="py-1.5 text-center"><BoolIcon value={src.zip} /></td>
              <td className="py-1.5 text-center"><BoolIcon value={src.state} /></td>
              <td className={`py-1.5 font-medium ${statusColor(src.status)}`}>{src.status || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {address.sources.some(s => s.notes) && (
        <div className="mt-1.5 space-y-0.5">
          {address.sources.filter(s => s.notes).map((s, i) => (
            <div key={i} className="text-[9px] text-[var(--text-muted)] italic">{s.vendor}: {s.notes}</div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Sources table ──────────────────────────────────────────────────

function SourcesSection({ sources }: { sources: SourceProfile[] }) {
  const [expanded, setExpanded] = useState(false)
  if (sources.length === 0) return null

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)]/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText size={13} className="text-violet-400" />
          <span className="text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider">All Data Sources</span>
          <span className="text-[10px] text-[var(--text-muted)]">{sources.length} sources</span>
        </div>
        <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-[var(--border-color)]">
          <table className="w-full text-[10px] mt-2">
            <thead>
              <tr className="text-[var(--text-muted)] text-left border-b border-[var(--border-color)]">
                <th className="pb-1 font-medium">Source</th>
                <th className="pb-1 font-medium">Vendor</th>
                <th className="pb-1 font-medium text-right">Coverage</th>
                <th className="pb-1 font-medium text-center">Consent</th>
                <th className="pb-1 font-medium text-center">Gov ID</th>
                <th className="pb-1 font-medium">API</th>
                <th className="pb-1 font-medium">Verifies</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((src, i) => (
                <tr key={i} className="border-b border-[var(--border-color)]/50">
                  <td className="py-1.5">
                    <span className="font-medium text-[var(--text-primary)]">{src.name}</span>
                    {src.production && <span className="ml-1 text-[8px] text-emerald-400">(live)</span>}
                  </td>
                  <td className="py-1.5 text-[var(--text-muted)]">{src.vendor}</td>
                  <td className="py-1.5 text-right">
                    {src.coverage != null ? (
                      <span className="flex items-center justify-end gap-1">
                        <MiniBar rate={src.coverage} width="w-10" />
                        <span className={`font-medium ${rateColor(src.coverage)}`}>{src.coverage}%</span>
                      </span>
                    ) : <span className="text-[var(--text-muted)]">—</span>}
                  </td>
                  <td className="py-1.5 text-center">
                    {src.consent_required
                      ? <Lock size={10} className="inline text-amber-400" />
                      : <Unlock size={10} className="inline text-emerald-400" />}
                  </td>
                  <td className="py-1.5 text-center"><BoolIcon value={src.government_id} /></td>
                  <td className="py-1.5">
                    <span className={`${src.api_status.toLowerCase().includes('untested') ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
                      {src.api_status}
                    </span>
                  </td>
                  <td className="py-1.5">
                    <div className="flex flex-wrap gap-0.5">
                      {src.verifies.slice(0, 4).map((v, j) => (
                        <span key={j} className="text-[8px] px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400">{v}</span>
                      ))}
                      {src.verifies.length > 4 && <span className="text-[8px] text-[var(--text-muted)]">+{src.verifies.length - 4}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Field Simulation spotlight ─────────────────────────────────────

function FieldSimulationSpotlight({ sources, userFields }: { sources: SourceProfile[]; userFields: string[] }) {
  if (userFields.length === 0) return null

  // For each source, check how many of the user's fields it requires vs has
  const sourceMatches = sources.map(src => {
    const required = src.mandatory_pii.map(p => p.toLowerCase())
    const userLower = userFields.map(f => f.toLowerCase())
    const missing = required.filter(r => !userLower.some(u => r.includes(u) || u.includes(r)))
    const matched = required.filter(r => userLower.some(u => r.includes(u) || u.includes(r)))
    return { src, matched, missing, usable: missing.length === 0 }
  })

  const usable = sourceMatches.filter(m => m.usable)
  const notUsable = sourceMatches.filter(m => !m.usable)

  return (
    <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-3">
      <div className="text-[11px] font-semibold text-violet-300 uppercase tracking-wider mb-2">Your Fields</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {userFields.map((f, i) => (
          <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30 font-medium capitalize">{f.replace('_', ' ')}</span>
        ))}
      </div>

      {usable.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-semibold text-emerald-400 mb-1">
            <CheckCircle2 size={10} className="inline mr-1" />{usable.length} source{usable.length !== 1 ? 's' : ''} can work with your fields
          </div>
          <div className="space-y-1">
            {usable.map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px] px-2 py-1 rounded border border-emerald-500/20 bg-emerald-500/5">
                <span className="font-medium text-[var(--text-primary)]">{m.src.name}</span>
                <span className="text-[var(--text-muted)]">{m.src.vendor}</span>
                {m.src.coverage != null && <span className="ml-auto text-[var(--text-muted)]">{m.src.coverage}% coverage</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {notUsable.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-amber-400 mb-1">
            <AlertTriangle size={10} className="inline mr-1" />Missing fields for {notUsable.length} source{notUsable.length !== 1 ? 's' : ''}
          </div>
          <div className="space-y-1">
            {notUsable.slice(0, 3).map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px] px-2 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)]">
                <span className="font-medium text-[var(--text-primary)]">{m.src.name}</span>
                <span className="text-[var(--text-muted)]">{m.src.vendor}</span>
                <span className="ml-auto text-amber-400">needs: {m.missing.join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Warnings ───────────────────────────────────────────────────────

function WarningsSection({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
      {warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-2 text-[10px] text-amber-400">
          <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />
          <span>{w}</span>
        </div>
      ))}
    </div>
  )
}

// ─── TLDR section ──────────────────────────────────────────────────

function TldrSection({ country, rulesetAsked }: { country: CountryProfile; rulesetAsked?: string | null }) {
  const lines: string[] = []
  const prod = country.production
  const opo = country.one_plus_one
  const tpt = country.two_plus_two

  if (rulesetAsked === '2+2') {
    // User asked about 2+2 specifically
    if (tpt.achievable && tpt.strength >= 60) {
      const srcNames = tpt.sources.map(s => `${s.name} (${s.vendor}, ${s.coverage}% coverage)`).join(' and ')
      lines.push(`**Yes, a 2+2 may be possible** using ${srcNames}.`)
      if (tpt.warnings.some(w => w.includes('share underlying'))) {
        lines.push('However, you should confirm these sources use different underlying databases — if they share the same data, it won\'t count as two independent sources.')
      }
    } else if (tpt.achievable) {
      const srcNames = tpt.sources.map(s => `${s.name} (${s.vendor}, ${s.coverage}% coverage)`).join(' and ')
      lines.push(`**A 2+2 is possible but coverage is weak** using ${srcNames}.`)
    } else {
      lines.push(`**A 2+2 is not achievable** for ${country.name} — we couldn't find two independent sources with sufficient coverage.`)
    }
    // Always mention 1+1 as fallback
    if (opo.achievable && opo.strength >= 60) {
      const bestSrc = opo.sources[0]
      let fallback = `Alternatively, a **1+1 is a strong option** via ${bestSrc.name} (${bestSrc.vendor}, ${bestSrc.coverage}% coverage)`
      if (prod && prod.entity_pass_rate >= 50) {
        const sourceLabel = prod.data_source === 'benchmark' ? 'benchmark production data' : 'live data'
        fallback += `, backed by a **${prod.entity_pass_rate}% production pass rate** from ${prod.best_source || sourceLabel}`
      }
      lines.push(fallback + '.')
    }
  } else if (rulesetAsked === '1+1') {
    // User asked about 1+1 specifically
    if (opo.achievable && opo.strength >= 60) {
      const bestSrc = opo.sources[0]
      let line = `**Yes, a 1+1 is achievable** via ${bestSrc.name} (${bestSrc.vendor}, ${bestSrc.coverage}% coverage)`
      if (prod && prod.entity_pass_rate >= 50) {
        const sourceLabel = prod.data_source === 'benchmark' ? 'benchmark customer data' : 'live traffic'
        line += `. Production data confirms a **${prod.entity_pass_rate}% pass rate** from ${prod.best_source || sourceLabel}`
      }
      lines.push(line + '.')
    } else if (opo.achievable) {
      lines.push(`**A 1+1 is possible but coverage is limited** (${opo.strength}%).`)
    } else {
      lines.push(`**A 1+1 is not achievable** for ${country.name} — no sources with sufficient coverage found.`)
    }
  } else {
    // General question — summarise what's possible
    if (prod && prod.entity_pass_rate >= 50) {
      const sourceLabel = prod.data_source === 'benchmark' ? 'benchmark customer' : (prod.best_source || 'the best source')
      lines.push(`**${country.name} looks good** — production data shows a **${prod.entity_pass_rate}% pass rate** from ${sourceLabel}.`)
    } else if (prod && prod.entity_pass_rate > 0) {
      lines.push(`**${country.name} has limited production performance** — best source shows ${prod.entity_pass_rate}% pass rate from ${prod.best_source || 'live data'}.`)
    }
    if (opo.achievable && opo.strength >= 60) {
      lines.push(`1+1 is **${opo.strength_label}** (${opo.strength}% coverage).`)
    }
    if (tpt.achievable && tpt.strength >= 60) {
      lines.push(`2+2 is **${tpt.strength_label}** (${tpt.strength}% coverage), but confirm sources are independent.`)
    }
  }

  // Consent callout
  if (rulesetAsked === '1+1' || rulesetAsked === '2+2' || rulesetAsked === 'any') {
    const relevantAssessment = rulesetAsked === '2+2' ? tpt : opo
    if (relevantAssessment.achievable) {
      if (relevantAssessment.any_consent_free) {
        lines.push('A consent-free option is available.')
      } else {
        lines.push('⚠️ All sources for this ruleset require consent.')
      }
    }
  }

  // Source data descriptions — what kind of data each source provides
  const relevantAssessment = rulesetAsked === '2+2' ? tpt : opo
  if (relevantAssessment.achievable && relevantAssessment.sources.length > 0) {
    const sourceMeta = relevantAssessment.sources.map(s => {
      const full = country.sources.find(fs => fs.name === s.name && fs.vendor === s.vendor)
      return { name: s.name, vendor: s.vendor, notes: full?.notes || null, pii: full?.mandatory_pii || [] }
    })

    // Data type descriptions
    const described = sourceMeta.filter(s => s.notes)
    if (described.length > 0) {
      const descLines = described.map(s => `**${s.name}** (${s.vendor}): ${s.notes}`)
      lines.push(`**About the recommended sources:** ${descLines.join(' · ')}`)
    }

    // Mandatory PII — combine into single merged list
    const allPii = [...new Set(sourceMeta.flatMap(s => s.pii))].sort()
    if (allPii.length > 0) {
      lines.push(`**Mandatory PII to run the match:** ${allPii.join(', ')}`)
    }
  }

  if (lines.length === 0) return null

  return (
    <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 px-3 py-2.5">
      <div className="text-[11px] font-semibold text-violet-300 uppercase tracking-wider mb-1.5">TL;DR</div>
      <div className="space-y-1">
        {lines.map((line, i) => (
          <p key={i} className="text-xs text-[var(--text-secondary)] leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <>{children}</>,
                strong: ({ children }) => <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>,
              }}
            >{line}</ReactMarkdown>
          </p>
        ))}
      </div>
    </div>
  )
}

// ─── Country profile card (COMPLETE — always all sections) ──────────

function CountryCard({ country, answerType, userFields, rulesetAsked }: { country: CountryProfile; answerType: string; userFields?: string[] | null; rulesetAsked?: string | null }) {
  const conf = CONFIDENCE[country.confidence]

  return (
    <div className="space-y-3">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">{getFlagEmoji(country.code)}</span>
          <h3 className="text-sm font-bold text-[var(--text-primary)]">{country.name}</h3>
          <Pill className={`${conf.bg} ${conf.text} ${conf.border}`}>{conf.label}</Pill>
          {country.recommended_vendor && (
            <span className="text-[9px] text-[var(--text-muted)]">Rec: {country.recommended_vendor}</span>
          )}
        </div>
        {country.production && (
          <span className={`text-lg font-bold ${rateColor(country.production.entity_pass_rate)}`}>
            {country.production.entity_pass_rate}%
          </span>
        )}
      </div>

      {/* ── TLDR ── */}
      <TldrSection country={country} rulesetAsked={rulesetAsked} />

      {/* ── Warnings ── */}
      <WarningsSection warnings={country.warnings} />

      {/* ── Spotlight (answer_type specific) ── */}
      {answerType === 'field_simulation' && userFields && userFields.length > 0 && (
        <FieldSimulationSpotlight sources={country.sources} userFields={userFields} />
      )}

      {/* ── Production ── */}
      {country.production && <ProductionSection prod={country.production} />}
      {!country.production && (
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3 text-center">
          <Activity size={16} className="mx-auto text-[var(--text-muted)] mb-1" />
          <span className="text-[10px] text-[var(--text-muted)]">No production data available for this country</span>
        </div>
      )}

      {/* ── Rulesets ── */}
      <RulesetsSection onePlusOne={country.one_plus_one} twoPlusTwo={country.two_plus_two} rulesetAsked={rulesetAsked} />

      {/* ── Consent ── */}
      <ConsentSection consent={country.consent} />

      {/* ── Gov IDs ── */}
      <GovIdSection govIds={country.gov_ids} />

      {/* ── Address ── */}
      <AddressSection address={country.address} />

      {/* ── All sources (expandable) ── */}
      <SourcesSection sources={country.sources} />
    </div>
  )
}

// ─── Multi-country comparison table ─────────────────────────────────

function ComparisonTable({ countries }: { countries: CountryProfile[] }) {
  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden">
      <table className="w-full text-[10px]">
        <thead>
          <tr className="text-[var(--text-muted)] text-left bg-[var(--bg-secondary)]">
            <th className="px-3 py-2 font-medium">Country</th>
            <th className="px-2 py-2 font-medium text-center">Confidence</th>
            <th className="px-2 py-2 font-medium text-right">Pass Rate</th>
            <th className="px-2 py-2 font-medium text-center">1+1</th>
            <th className="px-2 py-2 font-medium text-center">2+2</th>
            <th className="px-2 py-2 font-medium text-center">No Consent</th>
            <th className="px-2 py-2 font-medium text-center">Gov ID</th>
            <th className="px-2 py-2 font-medium text-right">Sources</th>
          </tr>
        </thead>
        <tbody>
          {countries.map((c, i) => {
            const conf = CONFIDENCE[c.confidence]
            const opo = STRENGTH[c.one_plus_one.strength_label]
            const tpt = STRENGTH[c.two_plus_two.strength_label]
            return (
              <tr key={i} className="border-t border-[var(--border-color)]/50 hover:bg-[var(--bg-tertiary)]/30">
                <td className="px-3 py-2">
                  <span className="mr-1">{getFlagEmoji(c.code)}</span>
                  <span className="font-medium text-[var(--text-primary)]">{c.name}</span>
                </td>
                <td className="px-2 py-2 text-center">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${conf.bg} ${conf.text} ${conf.border} border`}>{conf.label}</span>
                </td>
                <td className="px-2 py-2 text-right">
                  {c.production
                    ? <span className={`font-bold ${rateColor(c.production.entity_pass_rate)}`}>{c.production.entity_pass_rate}%</span>
                    : <span className="text-[var(--text-muted)]">—</span>}
                </td>
                <td className="px-2 py-2 text-center">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${opo.bg} ${opo.text} ${opo.border} border`}>
                    {c.one_plus_one.achievable ? opo.label : 'No'}
                  </span>
                </td>
                <td className="px-2 py-2 text-center">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${tpt.bg} ${tpt.text} ${tpt.border} border`}>
                    {c.two_plus_two.achievable ? tpt.label : 'No'}
                  </span>
                </td>
                <td className="px-2 py-2 text-center">
                  {c.consent.has_consent_free
                    ? <CheckCircle2 size={12} className="inline text-emerald-400" />
                    : <XCircle size={12} className="inline text-red-400/50" />}
                </td>
                <td className="px-2 py-2 text-center">
                  {c.gov_ids.length > 0
                    ? <CheckCircle2 size={12} className="inline text-emerald-400" />
                    : <XCircle size={12} className="inline text-red-400/50" />}
                </td>
                <td className="px-2 py-2 text-right text-[var(--text-muted)]">{c.sources.length}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Markdown components (for AI detail) ────────────────────────────

const mdComponents = {
  h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 className="text-sm font-bold text-[var(--text-primary)] mt-3 mb-1.5" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="text-xs font-bold text-[var(--text-primary)] mt-2.5 mb-1" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="text-xs font-semibold text-[var(--text-primary)] mt-2 mb-1" {...props}>{children}</h3>
  ),
  p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="text-xs text-[var(--text-secondary)] my-1 leading-relaxed" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="text-xs text-[var(--text-secondary)] my-1 ml-4 space-y-0.5 list-disc" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
    <ol className="text-xs text-[var(--text-secondary)] my-1 ml-4 space-y-0.5 list-decimal" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li className="text-xs text-[var(--text-secondary)] leading-relaxed" {...props}>{children}</li>
  ),
  strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <strong className="font-semibold text-[var(--text-primary)]" {...props}>{children}</strong>
  ),
  blockquote: ({ children, ...props }: React.BlockquoteHTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="my-1.5 pl-3 border-l-2 border-violet-500/50 italic text-[var(--text-muted)] text-xs" {...props}>{children}</blockquote>
  ),
}

// ─── Answer type labels ─────────────────────────────────────────────

const ANSWER_TYPE_LABELS: Record<string, string> = {
  pass_rates: 'Pass Rate Analysis',
  consent_check: 'Consent Check',
  ruleset_check: 'Ruleset Assessment',
  gov_id_format: 'Gov ID & Format',
  address_format: 'Address Requirements',
  field_simulation: 'Field Match Simulation',
  field_requirements: 'PII Requirements',
  general: 'Coverage Analysis',
}

// ─── Main component ─────────────────────────────────────────────────

export function StructuredAnswer({ answer_type, structured_data, ai_answer }: StructuredAnswerProps) {
  const [showAiDetail, setShowAiDetail] = useState(false)

  // No structured data — fall back to plain markdown
  if (!structured_data || !structured_data.countries?.length) {
    return (
      <div className="prose-sm">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{ai_answer}</ReactMarkdown>
      </div>
    )
  }

  const isMultiCountry = structured_data.countries.length > 1

  return (
    <div className="space-y-4">
      {/* Type badge */}
      <div className="flex items-center gap-2">
        <Pill className="bg-violet-500/15 text-violet-400 border-violet-500/30 uppercase tracking-wide">
          {ANSWER_TYPE_LABELS[answer_type] || answer_type}
        </Pill>
        {isMultiCountry && (
          <span className="text-[10px] text-[var(--text-muted)]">{structured_data.countries.length} countries</span>
        )}
      </div>

      {/* Multi-country comparison table */}
      {isMultiCountry && <ComparisonTable countries={structured_data.countries} />}

      {/* Country profile cards */}
      {structured_data.countries.map((c, i) => (
        <div key={i} className={`${isMultiCountry ? 'border-t border-[var(--border-color)] pt-4' : ''}`}>
          <CountryCard country={c} answerType={answer_type} userFields={structured_data.user_fields} rulesetAsked={structured_data.ruleset_asked} />
        </div>
      ))}

      {/* AI detail (collapsible) */}
      <div className="border-t border-[var(--border-color)] pt-3">
        <button
          onClick={() => setShowAiDetail(!showAiDetail)}
          className="flex items-center gap-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
        >
          <ChevronDown size={12} className={`transition-transform ${showAiDetail ? 'rotate-180' : ''}`} />
          {showAiDetail ? 'Hide' : 'Show'} AI Analysis
        </button>
        {showAiDetail && (
          <div className="mt-2 pl-3 border-l-2 border-violet-500/20">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{ai_answer}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────

function getFlagEmoji(countryCode: string): string {
  try {
    const code = countryCode.toUpperCase()
    return String.fromCodePoint(
      ...code.split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
    )
  } catch {
    return ''
  }
}
